import { Slider as SliderPrimitive } from "@base-ui/react/slider";
import * as React from "react";

import { cn } from "@/lib/utils";

function Slider({
	className,
	defaultValue,
	value,
	min = 0,
	max = 100,
	...props
}: SliderPrimitive.Root.Props) {
	const thumbKeysRef = React.useRef<string[]>([]);
	const nextThumbIdRef = React.useRef(0);
	const _values = React.useMemo(
		() =>
			Array.isArray(value)
				? value
				: typeof value === "number"
					? [value]
					: Array.isArray(defaultValue)
						? defaultValue
						: typeof defaultValue === "number"
							? [defaultValue]
							: [min],
		[value, defaultValue, min],
	);

	if (thumbKeysRef.current.length < _values.length) {
		thumbKeysRef.current = [
			...thumbKeysRef.current,
			...Array.from(
				{ length: _values.length - thumbKeysRef.current.length },
				() => `thumb-${nextThumbIdRef.current++}`,
			),
		];
	} else if (thumbKeysRef.current.length > _values.length) {
		thumbKeysRef.current = thumbKeysRef.current.slice(0, _values.length);
	}

	return (
		<SliderPrimitive.Root
			className={cn("data-horizontal:w-full data-vertical:h-full", className)}
			data-slot="slider"
			defaultValue={defaultValue}
			value={value}
			min={min}
			max={max}
			thumbAlignment="edge"
			{...props}
		>
			<SliderPrimitive.Control className="relative flex w-full touch-none items-center select-none data-disabled:opacity-50 data-vertical:h-full data-vertical:min-h-40 data-vertical:w-auto data-vertical:flex-col">
				<SliderPrimitive.Track
					data-slot="slider-track"
					className="bg-muted relative grow overflow-hidden rounded-full select-none data-horizontal:h-1 data-horizontal:w-full data-vertical:h-full data-vertical:w-1"
				>
					<SliderPrimitive.Indicator
						data-slot="slider-range"
						className="bg-primary select-none data-horizontal:h-full data-vertical:w-full"
					/>
				</SliderPrimitive.Track>
				{thumbKeysRef.current.map((thumbKey) => (
					<SliderPrimitive.Thumb
						data-slot="slider-thumb"
						key={thumbKey}
						className="border-ring ring-ring/50 relative block size-3 shrink-0 rounded-full border bg-white transition-[color,box-shadow] select-none after:absolute after:-inset-2 hover:ring-3 focus-visible:ring-3 focus-visible:outline-hidden active:ring-3 disabled:pointer-events-none disabled:opacity-50"
					/>
				))}
			</SliderPrimitive.Control>
		</SliderPrimitive.Root>
	);
}

export { Slider };
